import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DOCUMENT_STATUS_LABEL_KEYS, DocumentEventResponseDto } from '@taxora-shared-common/util';

interface AuditEventDisplay extends DocumentEventResponseDto {
  icon: string;
  text: string;
}

@Component({
  selector: 'lib-document-audit-trail',
  templateUrl: './document-audit-trail.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, TranslatePipe],
})
export class DocumentAuditTrail {
  private readonly translate = inject(TranslateService);

  readonly events = input<DocumentEventResponseDto[]>([]);

  protected readonly sortedEvents = computed<AuditEventDisplay[]>(() =>
    [...this.events()]
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .map((event) => ({ ...event, icon: this.resolveIcon(event), text: this.resolveText(event) })),
  );

  private resolveText(event: DocumentEventResponseDto): string {
    const name = `${event.user.firstName} ${event.user.lastName}`;
    switch (event.type) {
      case 'UPLOADED':
        return this.translate.instant('common.documents.audit.uploaded', { name });
      case 'STATUS_CHANGED': {
        const from = event.oldStatus
          ? this.translate.instant(DOCUMENT_STATUS_LABEL_KEYS[event.oldStatus])
          : '—';
        const to = event.newStatus
          ? this.translate.instant(DOCUMENT_STATUS_LABEL_KEYS[event.newStatus])
          : '—';
        return this.translate.instant('common.documents.audit.statusChanged', { from, to, name });
      }
      case 'CLARIFICATION_REQUESTED':
        return this.translate.instant('common.documents.audit.clarificationRequested', { name });
      case 'CLARIFICATION_ANSWERED':
        return this.translate.instant('common.documents.audit.clarificationAnswered', { name });
      case 'DELETED':
        return this.translate.instant('common.documents.audit.deleted', {
          reason: event.reason ?? '—',
        });
      default:
        return event.type;
    }
  }

  private resolveIcon(event: DocumentEventResponseDto): string {
    switch (event.type) {
      case 'UPLOADED':
        return 'pi-upload';
      case 'STATUS_CHANGED':
        return 'pi-refresh';
      case 'CLARIFICATION_REQUESTED':
        return 'pi-comment';
      case 'CLARIFICATION_ANSWERED':
        return 'pi-reply';
      case 'DELETED':
        return 'pi-trash';
      default:
        return 'pi-circle';
    }
  }
}
